type EventValue = {
  type?: string;
  value?: string;
};

// Duck-typed on purpose — mirrors the sibling filters' SentryEventLike so
// tests can pass plain object literals with no @sentry/* mocking.
type SentryEventLike = {
  message?: string;
  exception?: {
    values?: EventValue[];
  };
};

// Chromium raises this when a transaction is opened against a connection that
// has already begun closing — the tab is unloading, or `close()` ran while a
// background flush was still queued. The phrase only ever appears on that
// path, so it is the durable half of this check.
//
// Seen in full as:
//   InvalidStateError: Failed to execute 'transaction' on 'IDBDatabase':
//   The database connection is closing.
const CLOSING_CONNECTION_MESSAGE = 'database connection is closing';

// Chromium's IndexedDB backend surfaces a failed blob write as
// `UnknownError: Internal error. Failed to write blobs (IOError)`. Both
// fragments are required: `IOError` alone shows up in unrelated storage
// errors, and a bare `Failed to write blobs` is not a string Chromium emits
// on its own.
const BLOB_WRITE_FAILURE_FRAGMENTS = ['Failed to write blobs', 'IOError'];

const isIndexedDbTeardownRace = (text: string): boolean => {
  if (text.toLowerCase().includes(CLOSING_CONNECTION_MESSAGE)) {
    return true;
  }
  return BLOB_WRITE_FAILURE_FRAGMENTS.every((fragment) =>
    text.includes(fragment)
  );
};

/**
 * Drops the IndexedDB write errors raised when the browser tears down the
 * connection underneath Apollo's background cache persistence (Sentry
 * FRONTEND-2YX, FRONTEND-30Q and FRONTEND-30K, TA-10956).
 *
 * On web the `CachePersistor` in `useApolloClient.tsx` is configured with
 * `trigger: 'background'`, so it flushes the normalised cache to IndexedDB
 * (via `AsyncStorageWrapper`) off the main flow — including as the tab is
 * going away. When the page unloads, the user navigates, or a cache purge
 * races an in-progress flush, the connection is reclaimed mid-write and the
 * pending transaction rejects.
 *
 * There is no signal here and nothing to act on. The write is a best-effort
 * cache warm-up for the *next* session: losing it costs one cold fetch on the
 * next load and nothing else. No user-visible state is lost, because the cache
 * being persisted is a copy of what Apollo already served from memory. The
 * stack points at whichever route happened to be open (`/projects/allprojects`
 * on the primary issue) rather than at anything that caused the teardown.
 *
 * Deliberately narrow: only the closing-connection message, and only a blob
 * write that failed with `IOError`. Every other IndexedDB failure mode still
 * reports — `QuotaExceededError` (out of disk), `VersionError`, a blocked
 * upgrade, or a failure opening the database at all. Those are real and
 * actionable; these two are not.
 *
 * Known trade-off: `Failed to write blobs (IOError)` is not exclusively a
 * teardown signature — Chromium also raises it for a genuinely unwritable
 * profile directory (failing disk, corrupt IndexedDB store). We accept that
 * because the consequence is identical either way: background cache
 * persistence is best-effort, the app degrades to fetching from the network,
 * and a disk that cannot be written to will surface through failure modes
 * this filter does not touch — `QuotaExceededError` and open failures both
 * still report.
 *
 * Matched on message text rather than stack shape. The web bundle is minified
 * and these arrive as async IndexedDB rejections, so the frames are either
 * absent or mangled past the point of identifying the persistence layer —
 * gating on them would mean the filter silently stopped matching. The two
 * message fragments above are specific enough to carry the check on their own,
 * which is the same call the sibling `shouldDropApolloTeardownInvariant`
 * makes.
 */
export const shouldDropIndexedDbTeardownRace = (
  event: SentryEventLike
): boolean => {
  const candidates = [
    event.message,
    ...(event.exception?.values?.map((value) => value.value ?? '') ?? []),
  ];
  return candidates.some((text) => !!text && isIndexedDbTeardownRace(text));
};
